/**
 * `/select-company` — see `web/shapes/select-company.md`.
 *
 * A session belongs to exactly one company (API_CONTRACT §2.1). A person holding two
 * or more memberships logs in with `active_company_id: null` and
 * `company_selection_required: true`, can read `/auth/me`, and gets
 * `403 WORKFORCE_COMPANY_SELECTION_REQUIRED` from every business endpoint until one
 * is chosen.
 *
 * `POST /api/v1/auth/switch-company` is specified and **not mounted** — see
 * `src/modules/auth/routes.ts`. So this screen reports the state and does not offer a
 * control that would 404. Each membership is a row with its `enabled` flag rendered
 * as icon **and** text, because `DESIGN.md` §4's "not by colour alone" covers status
 * as well as severity; when the endpoint lands, each row becomes a button and nothing
 * else here changes.
 */

import type { ReactNode } from 'react';

import { useSession } from '../auth/SessionProvider';
import { useT } from '../i18n';
import { Icon } from '../states/icons';
import { RetryAction } from '../states/RetryAction';
import { StatusNote } from '../states/StatusNote';
import { ErrorCodeLine } from '../states/ErrorCodeLine';
import { ScreenHeading } from '../shell/ScreenHeading';

export function SelectCompanyScreen(): ReactNode {
  const t = useT();
  const { user, refresh, signOut } = useSession();
  if (user === null) return null;

  return (
    <div className="wp-centred">
      {/* The desk edge: the shell's own 240px rail, with the brand at the top of it
          and no nav. Paper fills the rest of the page (finish review, fix 6). */}
      <div className="wp-door__edge">
        <p className="wp-door__brand">{t('app.name')}</p>
      </div>
      <div className="wp-centred__inner wp-centred__inner--wide wp-stack">
        <ScreenHeading>{t('selectCompany.title')}</ScreenHeading>
        <p>{t('selectCompany.body', { count: user.companies.length })}</p>

        <ul className="wp-rows" data-testid="company-list">
          {user.companies.map((company) => (
            <li className="wp-row" key={company.company_id}>
              <span>{company.name}</span>
              <span className="wp-flag">
                <Icon name={company.enabled ? 'circle-check' : 'ban'} />
                {company.enabled ? t('selectCompany.enabled') : t('selectCompany.disabled')}
              </span>
            </li>
          ))}
        </ul>

        <StatusNote
          severity="info"
          title={t('selectCompany.unavailable')}
          testId="select-company-unavailable"
          actions={
            <>
              <RetryAction onRetry={refresh} testId="select-company-retry" />
              <button
                type="button"
                className="wp-button"
                onClick={() => void signOut()}
                data-testid="select-company-signout"
              >
                {t('common.signOut')}
              </button>
            </>
          }
        >
          <ErrorCodeLine code="WORKFORCE_COMPANY_SELECTION_REQUIRED" />
        </StatusNote>
      </div>
    </div>
  );
}
